import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft } from 'lucide-react';
import Logo from '../../components/common/Logo.tsx';
import SubmitProposalButton from '../../components/common/SubmitProposalButton.tsx';
import SolutionProduct from '../../components/proposal/SolutionProduct.tsx';
import ProcessComparison from '../../components/proposal/ProcessComparison.tsx';
import ProfitRiskAnalysis from '../../components/proposal/ProfitRiskAnalysis.tsx';
import UseCaseComparison from '../../components/proposal/UseCaseComparison.tsx';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'rgba(26, 26, 46, 0.1)',
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(8),
}));

const Header = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  zIndex: 100,
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  color: '#8a2be2',
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.1)',
  },
}));

// Mock solution data
const solutions = [
  {
    id: '1',
    title: 'AI-Powered Customer Service Platform',
    description: 'Intelligent platform that handles routine inquiries automatically and provides agents with AI assistance for complex issues',
    purpose: 'Improve customer satisfaction while reducing support costs',
    scaleMetrics: [
      { name: 'Volume', value: 8, color: '#4287f5' },
      { name: 'Velocity', value: 9, color: '#00CC99' },
      { name: 'Variety', value: 7, color: '#8A2BE2' },
      { name: 'Variability', value: 6, color: '#FFA500' },
      { name: 'Value', value: 9, color: '#FF4500' },
    ],
    successCriteria: [
      { id: '1', description: 'Reduce average response time by 50%' },
      { id: '2', description: 'Maintain customer satisfaction above 90%' },
      { id: '3', description: 'Automate handling of at least 60% of inquiries' },
    ],
    definitionOfDone: [
      { id: '1', description: 'System handles all common customer inquiries without human intervention' },
      { id: '2', description: 'Integration with existing CRM complete' },
      { id: '3', description: 'Agent dashboard provides real-time AI suggestions' },
    ],
    currentProcess: {
      title: 'Current Support Process',
      description: 'Manual ticket-based system with limited automation',
      steps: [
        { id: '1', description: 'Customer submits inquiry through web form or email' },
        { id: '2', description: 'Support agent manually reviews and categorizes ticket' },
        { id: '3', description: 'Agent researches solution in knowledge base' },
        { id: '4', description: 'Agent responds to customer with solution' },
        { id: '5', description: 'Ticket remains open until customer confirms resolution' },
      ],
    },
    futureProcess: {
      title: 'AI-Powered Support Process',
      description: 'Automated system with AI assistance for complex issues',
      steps: [
        { id: '1', description: 'Customer submits inquiry through any channel (web, email, chat)' },
        { id: '2', description: 'AI automatically categorizes and prioritizes inquiry' },
        { id: '3', description: 'Common inquiries are answered immediately by AI' },
        { id: '4', description: 'Complex issues are routed to agents with AI-suggested solutions' },
        { id: '5', description: 'System follows up automatically to confirm resolution' },
      ],
    },
    financials: {
      current: [
        { id: '1', name: 'Support staff costs', amount: 450000, type: 'risk' },
        { id: '2', name: 'Training expenses', amount: 75000, type: 'risk' },
        { id: '3', name: 'Customer churn due to slow response', amount: 120000, type: 'risk' },
        { id: '4', name: 'Current support revenue', amount: 350000, type: 'profit' },
      ],
      future: [
        { id: '1', name: 'Platform implementation', amount: 180000, type: 'risk' },
        { id: '2', name: 'Ongoing maintenance', amount: 45000, type: 'risk' },
        { id: '3', name: 'Reduced support staff costs', amount: 250000, type: 'profit' },
        { id: '4', name: 'Improved customer retention', amount: 180000, type: 'profit' },
        { id: '5', name: 'New customer acquisition', amount: 220000, type: 'profit' },
      ],
    },
    useCases: {
      current: [
        {
          id: '1',
          title: 'Manual Ticket Processing',
          description: 'Support agents manually process each ticket, taking 2-3 hours on average to resolve customer inquiries.',
        },
        {
          id: '2',
          title: 'Knowledge Base Lookup',
          description: 'Agents search through knowledge base articles to find solutions, often with inconsistent results.',
        },
      ],
      future: [
        {
          id: '1',
          title: 'Automated Response System',
          description: 'AI automatically responds to common inquiries within seconds, with 90% accuracy.',
        },
        {
          id: '2',
          title: 'Agent Assistance Dashboard',
          description: 'Support agents receive AI-generated suggestions for complex issues, reducing resolution time by 40%.',
        },
        {
          id: '3',
          title: 'Proactive Customer Outreach',
          description: 'System identifies potential issues before customers report them and initiates proactive support.',
        },
      ],
    },
  },
  {
    id: '2',
    title: 'Predictive Maintenance System',
    description: 'Create a system that predicts equipment failures before they occur using machine learning and IoT sensors',
    purpose: 'Reduce downtime and maintenance costs while extending equipment lifespan',
    scaleMetrics: [
      { name: 'Volume', value: 7, color: '#4287f5' },
      { name: 'Velocity', value: 8, color: '#00CC99' },
      { name: 'Variety', value: 9, color: '#8A2BE2' },
      { name: 'Variability', value: 7, color: '#FFA500' },
      { name: 'Value', value: 9, color: '#FF4500' },
    ],
    successCriteria: [
      { id: '1', description: 'Reduce unplanned downtime by 75%' },
      { id: '2', description: 'Decrease maintenance costs by 30%' },
      { id: '3', description: 'Extend average equipment lifespan by 20%' },
      { id: '4', description: 'Achieve 85% accuracy in failure predictions' },
    ],
    definitionOfDone: [
      { id: '1', description: 'IoT sensors installed on all critical equipment' },
      { id: '2', description: 'Machine learning models trained and validated with historical data' },
      { id: '3', description: 'Real-time monitoring dashboard implemented for maintenance team' },
      { id: '4', description: 'Alert system integrated with existing maintenance workflow' },
    ],
    currentProcess: {
      title: 'Current Maintenance Process',
      description: 'Scheduled maintenance with reactive repairs when failures occur',
      steps: [
        { id: '1', description: 'Perform scheduled maintenance based on fixed intervals' },
        { id: '2', description: 'Wait for equipment failure or malfunction reports' },
        { id: '3', description: 'Diagnose problem after failure occurs' },
        { id: '4', description: 'Order replacement parts as needed' },
        { id: '5', description: 'Repair or replace equipment, causing downtime' },
      ],
    },
    futureProcess: {
      title: 'Predictive Maintenance Process',
      description: 'Data-driven maintenance based on actual equipment condition',
      steps: [
        { id: '1', description: 'Continuously collect data from IoT sensors on equipment' },
        { id: '2', description: 'Analyze data patterns to identify potential failure indicators' },
        { id: '3', description: 'Generate maintenance alerts before failures occur' },
        { id: '4', description: 'Schedule maintenance during planned downtime' },
        { id: '5', description: 'Perform targeted maintenance only on components that need it' },
      ],
    },
    financials: {
      current: [
        { id: '1', name: 'Unplanned downtime costs', amount: 580000, type: 'risk' },
        { id: '2', name: 'Emergency repair premiums', amount: 120000, type: 'risk' },
        { id: '3', name: 'Excess inventory costs', amount: 95000, type: 'risk' },
        { id: '4', name: 'Current maintenance revenue', amount: 320000, type: 'profit' },
      ],
      future: [
        { id: '1', name: 'System implementation', amount: 180000, type: 'risk' },
        { id: '2', name: 'IoT sensors and installation', amount: 85000, type: 'risk' },
        { id: '3', name: 'Reduced downtime savings', amount: 430000, type: 'profit' },
        { id: '4', name: 'Extended equipment lifespan', amount: 210000, type: 'profit' },
        { id: '5', name: 'Optimized parts inventory', amount: 75000, type: 'profit' },
      ],
    },
    useCases: {
      current: [
        {
          id: '1',
          title: 'Scheduled Maintenance',
          description: 'Regular maintenance performed at fixed intervals regardless of equipment condition, often resulting in unnecessary work.',
        },
        {
          id: '2',
          title: 'Reactive Repairs',
          description: 'Emergency repairs conducted after equipment failure, causing unplanned downtime and production losses.',
        },
      ],
      future: [
        {
          id: '1',
          title: 'Condition-Based Alerts',
          description: 'System monitors equipment health in real-time and alerts maintenance team when parameters indicate potential issues.',
        },
        {
          id: '2',
          title: 'Failure Prediction',
          description: 'Machine learning algorithms predict specific component failures weeks in advance with 85% accuracy.',
        },
        {
          id: '3',
          title: 'Optimized Maintenance Scheduling',
          description: 'System automatically suggests optimal maintenance windows to minimize impact on production.',
        },
      ],
    },
  },
];

const SolutionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const solution = solutions.find(sol => sol.id === id);

  // Transform financial data to match the expected format
  const transformFinancialData = (items: any[]) => {
    return items.map(item => ({
      id: item.id,
      description: item.name, // Map 'name' to 'description'
      amount: item.amount,
      type: item.type as 'profit' | 'risk'
    }));
  };

  if (!solution) {
    return (
      <>
        <Header>
          <Logo size="medium" />
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={24} />
          </BackButton>
        </Header>
        <Container>
          <Box sx={{ mt: 10, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: '#fff' }}>
              Solution not found
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header>
        <Logo size="medium" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SubmitProposalButton text="Create Proposal" />
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={24} />
          </BackButton>
        </Box>
      </Header>

      <PageContainer>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <SolutionProduct
                title={solution.title}
                description={solution.description}
                purpose={solution.purpose}
                scaleMetrics={solution.scaleMetrics}
                successCriteria={solution.successCriteria}
                definitionOfDone={solution.definitionOfDone}
              />
            </Grid>

            <Grid item xs={12}>
              <ProcessComparison
                currentTitle={solution.currentProcess.title}
                currentDescription={solution.currentProcess.description}
                currentSteps={solution.currentProcess.steps}
                futureTitle={solution.futureProcess.title}
                futureDescription={solution.futureProcess.description}
                futureSteps={solution.futureProcess.steps}
              />
            </Grid>

            <Grid item xs={12}>
              <ProfitRiskAnalysis
                currentItems={transformFinancialData(solution.financials.current)}
                futureItems={transformFinancialData(solution.financials.future)}
              />
            </Grid>

            <Grid item xs={12}>
              <UseCaseComparison
                currentUseCases={solution.useCases.current}
                futureUseCases={solution.useCases.future}
              />
            </Grid>
          </Grid>
        </Container>
      </PageContainer>
    </>
  );
};

export default SolutionDetail; 